/**
 * `simulateShot` — the single public entry point to the physics (ARCHITECTURE.md §5.1).
 *
 * The WHOLE shot is simulated up front, then the caller animates the recorded
 * keyframes. That is what lets the Cloudflare Worker replay a shot headless, makes
 * replays data rather than recordings, and decouples a 480 Hz simulation from a phone
 * that sometimes delivers 40 fps.
 *
 * ⚠️ The full outcome therefore exists before the player sees a single frame. Nothing
 * outside the animation player may read `finalState` or `events` ahead of the
 * animation clock — no early sound, no aim guide that knows the ball drops.
 *
 * Scope so far: fixed-step integration, the cloth model (PHY-002), ball-to-ball
 * contact (PHY-003), cushions (PHY-004) and pockets (PHY-005).
 *
 * `geometry` is optional. Without it the table is an open plane, which is what the
 * physics tests below PHY-004 use — a ball then simply rolls until friction stops it.
 */
import { resolveBallContact, timeOfImpact } from './ball-collision.ts';
import { cushionContact, resolveCushionContact, timeToCushion } from './cushion.ts';
import { pocketReached, pot, resolveTipContact, timeToTip } from './pocket.ts';
import { DEFAULT_PHYSICS, type PhysicsConfig } from './config.ts';
import { createWriter } from './keyframes.ts';
import { cloneState, snapshot, type TableState } from './state.ts';
import { roundJawTips, type TableGeometry } from './table.ts';
import { CUE_BALL, type ShotInput, type SimEvent, type SimResult } from './types.ts';

/** The shot direction is supplied pre-rounded to 1e-6, so allow a little slack. */
const UNIT_VECTOR_TOLERANCE = 1e-5;

/**
 * How many contacts may be resolved within one 1/480 s step before the rest of the
 * step is simply advanced. Eight is far more than a real break produces; the bound
 * exists so a pathological state cannot spin here forever.
 */
const MAX_CONTACTS_PER_STEP = 8;

/**
 * Could ball `i` reach the edge of the table — and therefore any rail, jaw or tip —
 * within `seconds`?
 *
 * Conservative by construction: every surface lies on the table's bounding box or
 * outside it, so a ball whose distance to that box exceeds how far it can travel plus
 * the largest contact radius cannot touch any of them. One comparison replaces thirty
 * swept tests.
 */
function nearEdge(
  state: TableState,
  i: number,
  widthM: number,
  heightM: number,
  marginM: number,
  seconds: number,
): boolean {
  const x = state.xM[i] ?? 0;
  const y = state.yM[i] ?? 0;

  let nearest = x < widthM - x ? x : widthM - x;
  const nearestY = y < heightM - y ? y : heightM - y;
  if (nearestY < nearest) nearest = nearestY;
  if (nearest <= marginM) return true;

  const vx = state.vxMps[i] ?? 0;
  const vy = state.vyMps[i] ?? 0;
  const reach = Math.sqrt(vx * vx + vy * vy) * seconds + marginM;
  return nearest <= reach;
}

/** Move every ball on the table forward by `seconds` at its current velocity. */
function advance(state: TableState, count: number, seconds: number): void {
  for (let i = 0; i < count; i++) {
    if (state.potted[i] === 1) continue;
    state.xM[i] = (state.xM[i] ?? 0) + (state.vxMps[i] ?? 0) * seconds;
    state.yM[i] = (state.yM[i] ?? 0) + (state.vyMps[i] ?? 0) * seconds;
  }
}

/**
 * True if any ball still on the table is moving, or could set itself moving.
 *
 * A ball with backspin and no velocity is NOT at rest — friction on its contact patch
 * will drive it. A ball spinning only about the vertical axis IS at rest: nothing
 * couples that to the cloth, so waiting for it would add dead time a player would just
 * watch.
 */
function hasMotion(state: TableState, radiusM: number): boolean {
  for (let i = 0; i < state.count; i++) {
    if (state.potted[i] === 1) continue;
    if (state.vxMps[i] !== 0 || state.vyMps[i] !== 0) return true;
    const slipX = -radiusM * (state.wyRadS[i] ?? 0);
    const slipY = radiusM * (state.wxRadS[i] ?? 0);
    if (slipX !== 0 || slipY !== 0) return true;
  }
  return false;
}

function assertFinite(value: number, name: string): void {
  if (!Number.isFinite(value)) {
    throw new Error(`simulateShot: ${name} must be a finite number, got ${String(value)}`);
  }
}

/**
 * Rejects anything the Phase 4 Worker would also reject, so the client cannot
 * produce a shot log the server will refuse (ARCHITECTURE.md §6.3).
 */
export function validateShot(shot: ShotInput, config: PhysicsConfig = DEFAULT_PHYSICS): void {
  assertFinite(shot.dx, 'dx');
  assertFinite(shot.dy, 'dy');
  assertFinite(shot.power, 'power');
  assertFinite(shot.spinX, 'spinX');
  assertFinite(shot.spinY, 'spinY');

  if (shot.power < 0 || shot.power > 1) {
    throw new Error(`simulateShot: power must be within 0..1, got ${shot.power}`);
  }

  const lengthSquared = shot.dx * shot.dx + shot.dy * shot.dy;
  if (Math.abs(lengthSquared - 1) > UNIT_VECTOR_TOLERANCE) {
    throw new Error(
      `simulateShot: direction must be a unit vector, got (${shot.dx}, ${shot.dy}) with length² ${lengthSquared}`,
    );
  }

  const offsetSquared = shot.spinX * shot.spinX + shot.spinY * shot.spinY;
  const maxOffset = config.maxTipOffsetR;
  if (offsetSquared > maxOffset * maxOffset + UNIT_VECTOR_TOLERANCE) {
    throw new Error(
      `simulateShot: tip offset must be within ${maxOffset}R of centre, got (${shot.spinX}, ${shot.spinY})`,
    );
  }
}

export function simulateShot(
  state: TableState,
  shot: ShotInput,
  config: PhysicsConfig = DEFAULT_PHYSICS,
  geometry?: TableGeometry,
): SimResult {
  validateShot(shot, config);

  // The caller's state is never mutated: a shot is a pure function of what it was given.
  const working = cloneState(state);
  const { count } = working;
  const { dtS, keyframeStride } = config;

  /*
   * The cue strike (PHY-002). A tip offset of (spinX, spinY) in units of R, struck at
   * speed V, imparts |Δω| = 5·offset·V / (2R) about the axis through the contact.
   *
   * Follow/draw turn about the axis to the LEFT of travel, ẑ × d = (−dy, dx), which is
   * the same axis natural rolling turns about. Sidespin turns about +ẑ: striking right
   * of centre pushes the ball's right side forward, which is ANTICLOCKWISE seen from
   * above — the same r × F that gives the follow axis. It was written as clockwise
   * until PHY-007, which mirrored every English shot: right-hand English came off a
   * rail to the left and threw the object ball right (CLAUDE.md §13/L38).
   *
   * A useful check falls straight out of this: spinY = 0.4 gives ω = V/R exactly, the
   * textbook "2/5 of a radius above centre for natural roll".
   */
  const cueSpeed = shot.power * config.maxCueSpeedMps;
  const spinPerOffset = (2.5 * cueSpeed) / config.ballRadiusM;
  working.vxMps[CUE_BALL] = shot.dx * cueSpeed;
  working.vyMps[CUE_BALL] = shot.dy * cueSpeed;
  working.wxRadS[CUE_BALL] = spinPerOffset * shot.spinY * -shot.dy;
  working.wyRadS[CUE_BALL] = spinPerOffset * shot.spinY * shot.dx;
  working.wzRadS[CUE_BALL] = spinPerOffset * shot.spinX;

  const writer = createWriter(count);
  writer.push(0, working.xM, working.yM);

  const events: SimEvent[] = [];
  const maxSteps = Math.ceil(config.maxSimTimeS / dtS);

  const radiusM = config.ballRadiusM;
  const diameterM = radiusM + radiusM;
  const pockets = geometry?.pockets ?? [];
  /*
   * Jaw walls are ordinary cushion surfaces, so they join the cushion list rather than
   * getting a parallel code path. Each jaw tip is a curve tangent to its rail and its
   * jaw wall (PHY-010), so both are shortened to meet it, and the curve gets its own test.
   */
  const surfaces =
    geometry === undefined
      ? { cushions: [], tipCentres: [] }
      : roundJawTips(geometry, config.pocketJawTipRadiusM);
  const cushions = surfaces.cushions;
  const tips = surfaces.tipCentres;
  const contact = cushionContact(config);
  /*
   * A ball meets the curve at the same distance it meets a flat face, plus the curve's
   * radius, so rail, tip and jaw form one continuous surface with no step at the joins.
   * (It used to be the ball's full radius, a millimetre more than a rail's, D6.)
   */
  const tipContactRadiusM = contact.contactDistanceM + config.pocketJawTipRadiusM;

  /*
   * Broad phase. Testing every moving ball against every rail, jaw wall and jaw tip —
   * thirty surfaces on a real table — dominates the cost of a busy shot: measured at
   * 33 ms for nine balls in motion, against a 2 ms median.
   *
   * A ball far from the edge cannot reach any of them within one step, and "far from
   * the edge" is one comparison against the table's bounding box. Every surface lies
   * on that box or outside it, so this rejects without ever missing a contact.
   */
  const boundsW = geometry?.spec.widthM ?? 0;
  const boundsH = geometry?.spec.heightM ?? 0;
  const surfaceMarginM = contact.contactDistanceM + tipContactRadiusM;
  /* Speed lost per step while sliding. */
  const slideDecelPerStep = config.clothSlidingFriction * config.gravityMps2 * dtS;
  /*
   * Slip decays 7/2 times faster than speed does: the friction impulse both slows the
   * ball and spins it up, and the two effects close the gap together. This factor is
   * what makes the 5/7 law come out right.
   */
  const slipDecelPerStep = 3.5 * slideDecelPerStep;
  const rollDecelPerStep = config.clothRollingResistance * config.gravityMps2 * dtS;
  const spinDecelPerStep = (2.5 * config.clothSpinFriction * config.gravityMps2 * dtS) / radiusM;
  const restSpeed = config.restSpeedThresholdMps;

  /*
   * Which balls are sliding, so the roll transition is reported once, not per step.
   * Any ball in motion counts as sliding until the first step proves otherwise — a
   * shot struck at exactly the natural-roll offset then reports a sliding phase of
   * zero length, rather than reporting nothing at all.
   */
  const sliding = new Uint8Array(count);
  for (let i = 0; i < count; i++) {
    sliding[i] = working.vxMps[i] !== 0 || working.vyMps[i] !== 0 ? 1 : 0;
  }

  let step = 0;
  let hitTimeCap = false;
  // Checked BEFORE the first step, so a shot with nothing in motion takes zero time
  // rather than one wasted tick.
  let moving = hasMotion(working, radiusM);

  while (moving) {
    if (step >= maxSteps) {
      hitTimeCap = true;
      break;
    }
    step++;
    const tS = step * dtS;
    moving = false;

    // Ascending id order, always. See state.ts.
    for (let i = 0; i < count; i++) {
      if (working.potted[i] === 1) continue;

      let vx = working.vxMps[i] ?? 0;
      let vy = working.vyMps[i] ?? 0;
      let wx = working.wxRadS[i] ?? 0;
      let wy = working.wyRadS[i] ?? 0;
      const wz = working.wzRadS[i] ?? 0;

      /*
       * Velocity of the contact patch against the cloth: u = v + ω × (−R ẑ).
       * Friction acts on THIS, not on the ball's velocity — which is the whole reason
       * follow, draw and stun behave differently (ARCHITECTURE.md §5.3).
       *
       * Note wz contributes nothing to u: a point contact cannot feel spin about the
       * vertical axis. See docs/physics.md deviation D3.
       */
      const slipX = vx - radiusM * wy;
      const slipY = vy + radiusM * wx;
      // Only + - * / and Math.sqrt in here (ARCHITECTURE.md §5.5).
      const slip = Math.sqrt(slipX * slipX + slipY * slipY);

      if (slip > slipDecelPerStep) {
        // --- Sliding: friction opposes the slip, slowing the ball and spinning it up.
        const inv = 1 / slip;
        const ux = slipX * inv;
        const uy = slipY * inv;

        vx -= slideDecelPerStep * ux;
        vy -= slideDecelPerStep * uy;
        // dω/dt = (r × f) / I, with I = (2/5)mR² for a solid sphere.
        wx -= (2.5 * slideDecelPerStep * uy) / radiusM;
        wy += (2.5 * slideDecelPerStep * ux) / radiusM;
      } else {
        // --- Rolling: the patch is still, so only rolling resistance acts.
        if (sliding[i] === 1) {
          sliding[i] = 0;
          events.push({
            tS,
            type: 'roll',
            ids: [i],
            speedMps: Math.sqrt(vx * vx + vy * vy),
          });
        }

        const speed = Math.sqrt(vx * vx + vy * vy);
        if (speed <= rollDecelPerStep || speed < restSpeed) {
          vx = 0;
          vy = 0;
          wx = 0;
          wy = 0;
        } else {
          const scale = (speed - rollDecelPerStep) / speed;
          vx *= scale;
          vy *= scale;
          // Rolling ties spin to velocity exactly, so no drift can accumulate.
          wy = vx / radiusM;
          wx = -vy / radiusM;
        }
      }

      // Spin about the vertical axis decays on its own, coupled to nothing.
      let nextWz = 0;
      if (wz > spinDecelPerStep) nextWz = wz - spinDecelPerStep;
      else if (wz < -spinDecelPerStep) nextWz = wz + spinDecelPerStep;

      working.vxMps[i] = vx;
      working.vyMps[i] = vy;
      working.wxRadS[i] = wx;
      working.wyRadS[i] = wy;
      working.wzRadS[i] = nextWz;
      if (vx !== 0 || vy !== 0) moving = true;
    }

    /*
     * Positions advance AFTER every ball's velocity is settled, so contact is resolved
     * between balls that agree about what time it is. Within the step, the earliest
     * impact is found and resolved, then the remainder of the step continues — this is
     * the swept part, and it is what stops a fast ball stepping over a slow one.
     */
    let remainingS = dtS;
    for (let pass = 0; pass < MAX_CONTACTS_PER_STEP && remainingS > 0; pass++) {
      let soonestT = -1;
      let soonestI = -1;
      let soonestJ = -1;
      let soonestCushion = -1;

      // Fixed pair order, i < j ascending, so simultaneous contacts always resolve
      // in the same sequence (ARCHITECTURE.md §5.5).
      for (let i = 0; i < count; i++) {
        if (working.potted[i] === 1) continue;
        for (let j = i + 1; j < count; j++) {
          if (working.potted[j] === 1) continue;
          // A pair that is not moving relative to itself can never meet.
          if (working.vxMps[i] === working.vxMps[j] && working.vyMps[i] === working.vyMps[j]) {
            continue;
          }
          const hitT = timeOfImpact(working, i, j, remainingS, diameterM);
          if (hitT >= 0 && (soonestT < 0 || hitT < soonestT)) {
            soonestT = hitT;
            soonestI = i;
            soonestJ = j;
            soonestCushion = -1;
          }
        }
      }

      // Cushions, in declared order, after balls — so a ball caught exactly between
      // the two resolves against the other ball first, every time.
      for (let i = 0; i < count; i++) {
        if (working.potted[i] === 1) continue;
        if (working.vxMps[i] === 0 && working.vyMps[i] === 0) continue;
        if (!nearEdge(working, i, boundsW, boundsH, surfaceMarginM, remainingS)) continue;
        for (let c = 0; c < cushions.length; c++) {
          const cushion = cushions[c];
          if (cushion === undefined) continue;
          const hitT = timeToCushion(working, i, cushion, remainingS, contact);
          if (hitT >= 0 && (soonestT < 0 || hitT < soonestT)) {
            soonestT = hitT;
            soonestI = i;
            soonestJ = -1;
            soonestCushion = c;
          }
        }
      }

      // Rounded jaw tips, last in the ordering so a tie resolves against a flat
      // surface first — deterministically, whichever way it goes.
      let soonestTip = -1;
      for (let i = 0; i < count; i++) {
        if (working.potted[i] === 1) continue;
        if (working.vxMps[i] === 0 && working.vyMps[i] === 0) continue;
        if (!nearEdge(working, i, boundsW, boundsH, surfaceMarginM, remainingS)) continue;
        for (let k = 0; k < tips.length; k++) {
          const tip = tips[k];
          if (tip === undefined) continue;
          const hitT = timeToTip(working, i, tip, remainingS, tipContactRadiusM);
          if (hitT >= 0 && (soonestT < 0 || hitT < soonestT)) {
            soonestT = hitT;
            soonestI = i;
            soonestJ = -1;
            soonestCushion = -1;
            soonestTip = k;
          }
        }
      }

      if (soonestT < 0) break;

      advance(working, count, soonestT);
      remainingS -= soonestT;

      if (soonestTip >= 0) {
        const tip = tips[soonestTip];
        const approachSpeed =
          tip === undefined ? 0 : resolveTipContact(working, soonestI, tip, config);
        if (approachSpeed > 0) {
          events.push({
            tS: tS - remainingS,
            type: 'cushion',
            ids: [soonestI],
            speedMps: approachSpeed,
          });
        }
      } else if (soonestCushion >= 0) {
        const cushion = cushions[soonestCushion];
        const approachSpeed =
          cushion === undefined
            ? 0
            : resolveCushionContact(working, soonestI, cushion, config, contact);
        if (approachSpeed > 0) {
          events.push({
            tS: tS - remainingS,
            type: 'cushion',
            ids: [soonestI],
            speedMps: approachSpeed,
          });
        }
      } else {
        const closingSpeed = resolveBallContact(working, soonestI, soonestJ, config);
        if (closingSpeed > 0) {
          events.push({
            tS: tS - remainingS,
            type: 'ball-ball',
            ids: [soonestI, soonestJ],
            speedMps: closingSpeed,
          });
        }
      }
      moving = true;
    }

    if (remainingS > 0) advance(working, count, remainingS);

    /*
     * Has anything reached a throat? Checked after the step's motion is complete, in
     * ascending ball order. A ball only gets here by passing the jaws, which is what
     * makes reaching the throat mean something.
     */
    if (pockets.length > 0) {
      for (let i = 0; i < count; i++) {
        if (working.potted[i] === 1) continue;
        // A throat is on the boundary, so the same rejection applies.
        if (!nearEdge(working, i, boundsW, boundsH, surfaceMarginM, 0)) continue;
        if (pocketReached(working, i, pockets) < 0) continue;

        const speedMps = Math.sqrt(
          (working.vxMps[i] ?? 0) * (working.vxMps[i] ?? 0) +
            (working.vyMps[i] ?? 0) * (working.vyMps[i] ?? 0),
        );
        pot(working, i);
        events.push({ tS, type: 'pocket', ids: [i], speedMps });
        // The cue ball going down is a different thing entirely: it costs a penalty
        // stroke and the ball comes back (PRD §4). Recorded separately so the game
        // layer never has to work it out from ids.
        if (i === CUE_BALL) {
          events.push({ tS, type: 'scratch', ids: [i], speedMps });
        }
      }
    }

    if (step % keyframeStride === 0) {
      writer.push(tS, working.xM, working.yM);
    }
  }

  /*
   * Residual spin about the vertical axis is discarded once the table is still. It is
   * coupled to nothing, so it cannot change the outcome, and in a real game it would
   * dissipate long before the player takes the next shot. Zeroing it keeps the final
   * state canonical, which matters for replay and for hashing.
   *
   * Only when the shot genuinely came to rest, though. If the time cap fired, the shot
   * did not finish, the state is diagnostic rather than canonical, and throwing spin
   * away would destroy the one piece of evidence worth having.
   */
  if (!hitTimeCap) {
    for (let i = 0; i < count; i++) working.wzRadS[i] = 0;
  }

  const durationS = step * dtS;
  // Only if the loop did not already land on it, so frame times stay strictly increasing.
  if (writer.lastTimeS() !== durationS) {
    writer.push(durationS, working.xM, working.yM);
  }

  events.push({
    tS: durationS,
    type: hitTimeCap ? 'cap' : 'rest',
    ids: [],
    speedMps: 0,
  });

  return {
    keyframes: writer.finish(),
    events,
    finalState: snapshot(working),
    durationS,
    hitTimeCap,
  };
}
